import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePresetsContext } from './presetsContext';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName } from '@redux/selectors/selectors';
import { capitalizeFirstLetter, isErrorResponse } from '@utils/utils';
import { setSelectedMapFeatures } from '@redux/showInfo/showInfoReducer';
import { useDispatch } from 'react-redux';
import {
    GetAvailableSelectionSetById,
    PRESET_BY_ENTITY_NAME_KEY,
    PresetTypeKey,
} from '@api/presetsApi/types';
import { BaseEntity } from '@api/entities/BaseEntity';
import { serviceApi } from '@api/serviceApi';
import { useMap } from 'react-map-gl';
import { SelectedFeatureProperties } from '@redux/showInfo/types';
import { FeatureState } from '@redux/map/types';
import { requireToHighlightFeatures } from '@redux/map/map-reducer';
import { ApiError } from '@api/RestClient';
import { useEntityLoader } from '@features/entities/entity-form/model/useEntityLoader';

type ContextType = {
    loading: boolean;
    setLoading: (loading: boolean) => void;
    availableSelectionSets: BaseEntity[];
    getGroupSelectionSets: () => Promise<void>;
    getGroupSelectionSetById: (id: string) => Promise<GetAvailableSelectionSetById | ApiError>;
    createGroupSelectionSet: (setName: string, ids: string[]) => Promise<any>;
    deleteGroupSelectionSet: (id: string) => Promise<any>;
};

const GroupSelectionContext = createContext<ContextType>({} as ContextType);

const useGroupSelectionContext = () => {
    const ctx = useContext(GroupSelectionContext);
    if (!ctx) {
        throw new Error('useGroupSelectionContext must be used inside GroupSelectionProvider');
    }
    return ctx;
};

type ProviderProps = {
    entityName: string;
    sourceEntityName: string;
    children: React.ReactNode;
};

const GroupSelectionProvider = ({ entityName, sourceEntityName, children }: ProviderProps) => {
    const selectionSetEntityName = 'replancity_SelectionSet';
    const [loading, setLoading] = useState<boolean>(false);
    const [availableSelectionSets, setAvailableSelectionSets] = useState<BaseEntity[]>([]);
    const { selectedPresetId, selectedPresetEntityName } = usePresetsContext();
    const sourceId = useTypedSelector(state =>
        selectMapLayerIdByEntityName(state, sourceEntityName)
    );
    const { loading: isSetBeingDeleted, deleteFn } = useEntityLoader<any>({
        entityName: selectionSetEntityName,
    });
    const { mapboxMapId } = useMap();
    const dispatch = useDispatch();

    const getGroupSelectionSets = useCallback(async () => {
        const presetEntityName = PRESET_BY_ENTITY_NAME_KEY[selectedPresetEntityName];

        setLoading(true);
        const abortController = new AbortController();
        const resp = await serviceApi.getAvailableSelectionSets({
            presetId: selectedPresetId,
            presetType: capitalizeFirstLetter(presetEntityName) as Capitalize<PresetTypeKey>,
            abortSignal: abortController.signal,
        });

        if (resp && !isErrorResponse(resp)) {
            setAvailableSelectionSets(resp);
        }
        setLoading(false);
    }, [selectedPresetId, selectedPresetEntityName]);

    const getGroupSelectionSetById = useCallback(
        async (id: string) => {
            const abortController = new AbortController();
            const resp = await serviceApi.getSelectionSetById({
                selectionSetId: id,
                abortSignal: abortController.signal,
            });

            const map = mapboxMapId?.getMap();
            if (!map) {
                console.error('Replan. Map not found');
            }

            if (!isErrorResponse(resp) && map?.isStyleLoaded() && map?.getLayer(sourceId)) {
                const { targets } = resp;
                if (targets.length) {
                    const featureProperties: SelectedFeatureProperties[] = targets.map(target => ({
                        ...target,
                        layerId: sourceId,
                        entityName: sourceEntityName,
                        stateProperty: FeatureState.HIGHLIGHTED,
                    }));
                    dispatch(setSelectedMapFeatures(featureProperties));

                    dispatch(
                        requireToHighlightFeatures({
                            featureProperties,
                        })
                    );
                }
            }

            return resp;
        },
        [sourceId, sourceEntityName, mapboxMapId]
    );

    const createGroupSelectionSet = useCallback(
        async (setName: string, ids: string[]) => {
            const presetEntityName = PRESET_BY_ENTITY_NAME_KEY[selectedPresetEntityName];
            const abortController = new AbortController();

            const resp = await serviceApi.createSelectionSet({
                selectionSetCreateTask: {
                    presetId: selectedPresetId,
                    presetType: capitalizeFirstLetter(
                        presetEntityName
                    ) as Capitalize<PresetTypeKey>,
                    name: setName,
                    targets: ids,
                },
                abortSignal: abortController.signal,
            });
            if (!isErrorResponse(resp)) {
                const { id, name, _instanceName, _entityName } = resp;
                setAvailableSelectionSets(prev => [
                    ...prev,
                    { id, name, _instanceName, _entityName } as BaseEntity,
                ]);
            }
            return resp;
        },
        [selectedPresetId, selectedPresetEntityName]
    );

    const deleteGroupSelectionSet = useCallback(
        async (id: string) => {
            const resp = await deleteFn(id);
            if (!isErrorResponse(resp)) {
                setAvailableSelectionSets(prev => prev.filter(set => set.id !== id));
            }
            return resp;
        },
        [setAvailableSelectionSets]
    );

    useEffect(() => {
        void (async function () {
            await getGroupSelectionSets();
        })();
    }, [getGroupSelectionSets]);

    const value = useMemo(
        () => ({
            loading: loading || isSetBeingDeleted,
            setLoading,
            availableSelectionSets,
            getGroupSelectionSets,
            getGroupSelectionSetById,
            createGroupSelectionSet,
            deleteGroupSelectionSet,
        }),
        [
            loading,
            isSetBeingDeleted,
            setLoading,
            availableSelectionSets,
            getGroupSelectionSets,
            getGroupSelectionSetById,
            createGroupSelectionSet,
            deleteGroupSelectionSet,
            mapboxMapId,
        ]
    );

    return (
        <GroupSelectionContext.Provider value={value}>{children}</GroupSelectionContext.Provider>
    );
};

export { GroupSelectionProvider, useGroupSelectionContext };
